import React, { ReactElement, useEffect, useState } from "react"
import styles from "./carousel.module.css"
import { Foto } from "./downloadStrategies/downloadStrategies"
import { Icon } from "./icons/Icon"

interface Props {
    fotos: (Foto | undefined)[]
    onHuidigeFotoVeranderd: (nieuweFoto: Foto) => void
}

export const Carousel = (props: Props): ReactElement => {
    const { fotos, onHuidigeFotoVeranderd } = props

    const [huidigePagina, setHuidigePagina] = useState<number>(0)
    const [huidigeFoto, setHuidigeFoto] = useState<number>(0)
    const [gesleepteAfstand, setGesleepteAfstand] = useState(0)
    const [sleepStartPunt, setSleepStartPunt] = useState<{ x: number; y: number } | undefined>(undefined)
    const [komtVanKant, setKomtVanKant] = useState<"links" | "rechts" | undefined>(undefined)

    useEffect(() => {
        // Zorgen dat de index altijd staat op en bestaande foto
        setHuidigeFoto(Math.min(0, huidigeFoto % fotos.length))
        setHuidigePagina(0)
    }, [fotos])

    useEffect(() => {
        const huidigeFotoGegevens = fotos[huidigeFoto]
        if (huidigeFotoGegevens !== undefined) {
            onHuidigeFotoVeranderd(huidigeFotoGegevens)
            setGesleepteAfstand(0)
        }
    }, [huidigeFoto, fotos])

    const naarLinks = () => {
        setHuidigeFoto((huidigeFoto) => {
            const volgendeFoto = (fotos.length + huidigeFoto - 1) % fotos.length
            if (huidigeFoto === 0) {
                setHuidigePagina((huidigePagina) => huidigePagina - 1)
            }
            return volgendeFoto
        })
        setKomtVanKant("rechts")
    }

    const naarRechts = () => {
        setHuidigeFoto((huidigeFoto) => {
            const volgendeFoto = (fotos.length + huidigeFoto + 1) % fotos.length
            if (huidigeFoto === fotos.length - 1) {
                setHuidigePagina((huidigePagina) => huidigePagina + 1)
            }
            return volgendeFoto
        })
        setKomtVanKant("links")
    }

    /**
     * De beste pagina is kan links of rechts van de huidige foto liggen, want we willen "oneindig" kunnen blijven scrollen.
     * Deze functie bepaald of een gegeven foto het beste links of rechts kan staan van de huidige foto.
     * Hierbij is het het belangrijkste dat er altijd een afbeelding links en rechts beschikbaar is.
     */
    const bepaalBestePagina = (fotoIndex: number): number => {
        if (fotoIndex === 0 && huidigeFoto === fotos.length - 1 && (gesleepteAfstand > 0 || komtVanKant === "rechts")) {
            // De eerste foto moet alvast op de volgende pagina komen als we de laaste naar links swipen (dan verschijnt hij namelijk weer).
            // Hij moet daar ook nog even blijven staan als we net van de eerste foto weer direct naar de laatste toe gaan.
            return huidigePagina + 1
        } else if (fotoIndex === fotos.length - 1 && huidigeFoto === 0 && (gesleepteAfstand < 0 || komtVanKant === "links")) {
            // De laaste foto moet alvast op de vorige pagina komen als we de eerste naar rechts swipen (dan verschijnt hij namelijk weer).
            // Hij moet daar ook nog even blijven staan als we net van de laatste foto weer direct naar de eerste toe gaan.
            return huidigePagina - 1
        } else {
            return huidigePagina
        }
    }

    useEffect(() => {
        const toetsGedrukt = (event: KeyboardEvent) => {
            if (fotos.length > 1) {
                if (event.key === "ArrowLeft") {
                    naarLinks()
                } else if (event.key === "ArrowRight") {
                    naarRechts()
                }
            }
        }
        window.addEventListener("keyup", toetsGedrukt)

        return () => {
            window.removeEventListener("keyup", toetsGedrukt)
        }
    }, [])

    return (
        <div className={styles.carousel}>
            <div className={styles.mainImage}>
                <div className={styles.fotoTeller}>
                    {huidigeFoto + 1}/{fotos.length}
                </div>
                {fotos.length > 1 && (
                    <>
                        <div className={`${styles.arrow} ${styles.left}`} onClick={naarLinks} />
                        <div className={`${styles.arrow} ${styles.right}`} onClick={naarRechts} />
                    </>
                )}
                <div
                    className={`${styles.mainImageTrack} ${sleepStartPunt ? styles.dragging : ""}`}
                    style={{
                        transform: `translate(calc(-${huidigeFoto} * (100% + var(--distance-between-images)) - ${sleepStartPunt ? gesleepteAfstand : 0}px - ${
                            huidigePagina * fotos.length
                        } * (100% + var(--distance-between-images))))`,
                    }}
                    onPointerDown={(event) => {
                        if (fotos.length > 0) {
                            event.currentTarget.setPointerCapture(event.pointerId)
                            setSleepStartPunt({ x: event.clientX, y: event.clientY })
                            setGesleepteAfstand(0)
                            setKomtVanKant(undefined)
                        }
                    }}
                    onPointerMove={(event) => {
                        if (sleepStartPunt) {
                            setGesleepteAfstand(sleepStartPunt.x - event.clientX)
                        }
                    }}
                    onPointerUp={(event) => {
                        if (Math.abs(gesleepteAfstand) > event.currentTarget.clientWidth / 3) {
                            if (gesleepteAfstand < 0) {
                                naarLinks()
                            } else {
                                naarRechts()
                            }
                        }
                        setSleepStartPunt(undefined)
                    }}
                >
                    {fotos.map((foto, index) => (
                        <div style={{ transform: `translateX(calc(${bepaalBestePagina(index)} * ${fotos.length} * (100% + var(--distance-between-images))))` }}>
                            {foto ? <img key={foto.url} src={foto.url} onDragStart={(event) => event.preventDefault()}></img> : <Icon icon="spinner" />}
                        </div>
                    ))}
                </div>
            </div>
            <div className={styles.previews}>
                {fotos.map((foto, index) => (
                    <div className={styles.preview}>
                        {foto ? (
                            <img
                                key={foto.url}
                                src={foto.url}
                                onDragStart={(event) => event.preventDefault()}
                                onClick={() => {
                                    setKomtVanKant(undefined)
                                    setHuidigeFoto(index)
                                }}
                                className={index === huidigeFoto ? styles.selected : ""}
                            ></img>
                        ) : (
                            <Icon icon="spinner" />
                        )}
                    </div>
                ))}
            </div>
        </div>
    )
}
